// src/pages/AccountingBookkeeping.js
import React, { useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, Briefcase, ReceiptText, Calculator, DollarSign, BarChart2, TrendingUp, Handshake, Users } from 'lucide-react';

const AccountingBookkeeping = () => {
    // Intersection Observer logic for fade-in animations
    const observedSections = useRef(new Set());
    const setSectionRef = useCallback((node) => {
        if (node) {
            observedSections.current.add(node);
        }
    }, []);

    const handleSectionIntersect = useCallback((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up-active');
                // Remove initial opacity and transform classes if they were set for the animation
                entry.target.classList.remove('opacity-0', 'translate-y-10');
                if (entry.target.currentObserver) {
                    entry.target.currentObserver.unobserve(entry.target);
                }
            }
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleSectionIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1 // Trigger when 10% of the element is visible
        });

        observedSections.current.forEach(section => {
            if (section) {
                observer.observe(section);
                section.currentObserver = observer; // Store observer reference on the element
            }
        });

        return () => {
            observedSections.current.forEach(section => {
                if (section && section.currentObserver) {
                    section.currentObserver.unobserve(section);
                }
            });
            observer.disconnect();
            observedSections.current.clear();
        };
    }, [handleSectionIntersect]);

    const accountingTools = [
        {
            title: "Spreadsheet Software",
            desc: "Proficiency in advanced spreadsheet software for detailed financial modeling and analysis.",
            Icon: FileSpreadsheet, // Using Lucide icon component directly
            iconBg: "#4CAF50", // Green
            link: "/accounting-bookkeeping/spreadsheet-tools" // Placeholder link
        },
        {
            title: "Cloud Accounting Platforms",
            desc: "Expertise in popular cloud-based platforms for seamless, real-time financial management.",
            Icon: Briefcase, // Using Lucide icon component directly
            iconBg: "#C9B072", // Gold/Tan
            link: "/accounting-bookkeeping/cloud-platforms" // Placeholder link
        },
        {
            title: "Expense & Payroll Management",
            desc: "Utilizing specialized tools for accurate expense tracking and efficient payroll processing.",
            Icon: ReceiptText, // Using Lucide icon component directly
            iconBg: "#4CAF50", // Green
            link: "/accounting-bookkeeping/expense-payroll-tools" // Placeholder link
        },
        {
            title: "Financial Planning Tools",
            desc: "Leveraging robust tools for budgeting, forecasting, and strategic financial planning. Includes our Ratio Calculator.",
            Icon: Calculator, // Using Calculator icon from Lucide for the ratio calculator!
            iconBg: "#C9B072", // Gold/Tan
            link: "/accounting-bookkeeping/ratio-calculator" // Link to our Accounting Ratio Calculator page!
        },
    ];

    const coreServices = [
        { Icon: ReceiptText, title: "Daily Transaction Recording", delay: "0.1s", text: "Accurate and timely recording of all financial transactions to maintain up-to-date ledgers.", iconText: "🧾" },
        { Icon: DollarSign, title: "Bank & Credit Card Reconciliation", delay: "0.2s", text: "Matching your bank and credit card statements with your internal records to ensure accuracy.", iconText: "💲" },
        { Icon: Handshake, title: "Accounts Management", delay: "0.3s", text: "Efficiently manage your invoices, accounts payable, and accounts receivable for optimal cash flow.", iconText: "🤝" },
        { Icon: BarChart2, title: "Custom Financial Reporting", delay: "0.4s", text: "Generate insightful reports including Profit & Loss, Balance Sheets, and Cash Flow statements.", iconText: "📊" },
        { Icon: Users, title: "Payroll Processing", delay: "0.5s", text: "Accurate and compliant payroll services, ensuring your employees are paid on time, every time.", iconText: "👥" },
        { Icon: TrendingUp, title: "Tax Preparation Support", delay: "0.6s", text: "Assistance with gathering and organizing financial data, preparing you for tax season.", iconText: "📈" },
    ];


    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Global Styles for animations */}
            <style>
                {`
                .fade-in-up { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease-out, transform 0.8s ease-out; }
                .fade-in-up-active { opacity: 1; transform: translateY(0); }
                `}
            </style>

            {/* Section 1: Hero/Introduction - Blue Background with Image */}
            <section
                className="py-20 bg-[#0A1128] text-center px-6 bg-cover bg-center"
                style={{ backgroundImage: "url('https://i.imgur.com/4B0VjkG.png')" }}
            >
                <div className="container mx-auto bg-black bg-opacity-60 p-8 rounded-lg"> {/* Added overlay for readability */}
                    {/* Removed fade-in-up and ref={setSectionRef} from these elements */}
                    <h1 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-6">
                        Comprehensive Accounting & Bookkeeping Solutions
                    </h1>
                    <p className="text-xl md:text-2xl text-[#CCD2E3] max-w-3xl mx-auto mb-10">
                        At JAKOM, we offer meticulous accounting and bookkeeping services designed to keep your finances in perfect order, ensuring compliance and providing clear insights for strategic growth.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4"> {/* Removed fade-in-up and ref={setSectionRef} */}
                        <Link to="/get-a-quote" className="px-8 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                            Get an Accounting Quote
                        </Link>
                        <Link to="/accounting-bookkeeping/ratio-calculator" className="px-8 py-3 bg-[#C9B072] text-[#0A1128] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                            Quick Accounting Tools
                        </Link>
                    </div>
                </div>
            </section>

            {/* Section 2: Core Accounting Services - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-12 fade-in-up" ref={setSectionRef}>
                        Our Core Accounting Services
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {coreServices.map(({ Icon, title, delay, text, iconText }, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105"
                                ref={setSectionRef}
                                style={{ animationDelay: delay }}
                            >
                                <div className="p-4 bg-[#4CAF50] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center text-5xl">
                                    {iconText}
                                </div>
                                <h3 className="text-2xl font-bold text-[#0A1128] mb-3">{title}</h3>
                                <p className="text-gray-700 text-lg leading-relaxed">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section 3: Essential Accounting Tools - Blue Background (Now with Links) */}
            <section className="py-20 bg-[#0A1128] border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#F8F8F8] mb-12 fade-in-up" ref={setSectionRef}>
                        Essential Accounting Tools We Utilize
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                        {accountingTools.map((tool, idx) => (
                            <Link
                                to={tool.link} // Use the tool's specific link
                                key={idx}
                                className="bg-[#1A2238] rounded-xl p-6 flex flex-col items-center text-center shadow-xl border border-[#4CAF50] fade-in-up transform transition-transform duration-300 hover:scale-105 cursor-pointer"
                                ref={setSectionRef}
                                style={{ animationDelay: `${idx * 0.1}s` }} // Apply a staggered delay
                            >
                                <div className="p-4 rounded-full mb-4 inline-flex items-center justify-content-center" style={{ backgroundColor: tool.iconBg, color: '#0A1128' }}>
                                    {/* Render Lucide Icon or emoji based on what's available */}
                                    {tool.Icon ? <tool.Icon size={50} /> : <span className="text-5xl">{tool.iconText}</span>}
                                </div>
                                <h3 className="text-2xl font-bold text-[#F8F8F8] mb-3">{tool.title}</h3>
                                <p className="text-[#CCD2E3] text-lg leading-relaxed">{tool.desc}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>


            {/* Section 4: Call to Action - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-3xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-8 fade-in-up" ref={setSectionRef}>
                        Ready to Streamline Your Finances?
                    </h2>
                    <p className="text-xl text-[#333333] mb-10 fade-in-up" ref={setSectionRef}>
                        Let our expert team handle your accounting needs, so you can focus on what you do best.
                    </p>
                    <Link to="/contact-us" className="px-12 py-4 bg-[#4CAF50] text-[#F8F8F8] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse inline-flex items-center justify-content-center fade-in-up" ref={setSectionRef}>
                        Contact Our Accounting Team
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default AccountingBookkeeping;